/** Detects long-press or click interactions with movement tolerance for touch and mouse events. */
import { useRef } from 'react';
import { LONG_PRESS_DELAY_MS } from '@/config/constants';
import { useLongPressPointerHandlers } from '@/features/tasks/hooks/ui/use-interaction-long-press.helpers';

/**
 * Custom hook to detect a long press or a click, taking into account movement tolerance.
 * @param {function} onLongPress - Callback function for a long press.
 * @param {function} onClick - Callback function for a short click.
 * @param {object} [options]
 * @param {number} [options.ms=LONG_PRESS_DELAY_MS] - The duration in milliseconds to qualify as a long press.
 * @param {number} [options.moveThreshold=10] - The pixel distance the pointer can move before cancelling the press.
 * @returns {object} Event handlers to be spread onto a component.
 */
const MOVE_THRESHOLD_PX = 10;

const useLongPress = (
  onLongPress,
  onClick,
  { ms = LONG_PRESS_DELAY_MS, moveThreshold = MOVE_THRESHOLD_PX } = {},
) => {
  const timerRef = useRef(null);
  const startPointRef = useRef({ x: 0, y: 0 });
  const isLongPressTriggeredRef = useRef(false);
  const isMovementExceededRef = useRef(false);

  return useLongPressPointerHandlers({
    onLongPress,
    onClick,
    ms,
    moveThreshold,
    timerRef,
    startPointRef,
    isLongPressTriggeredRef,
    isMovementExceededRef,
  });
};

export default useLongPress;
